'use client';
import ScaleChip from '../ui/scaleChip';
import { DateTime } from 'luxon';
import { toTitleCase } from '../../utils/titleCase';
import { Noto_Music } from 'next/font/google';
import useSWR from 'swr';
import { toast } from 'sonner';
import { getTodayInLocalTimezone } from '../../utils/dateUtils';


const notoMusic = Noto_Music({ weight: '400', subsets: ['latin'] });

const fetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch data');
  return res.json();
};


export default function DailyPracticeLogDisplay({ session, dispatch }) {
  // Use the user's time zone to display the date consistently
  const today = getTodayInLocalTimezone();

  const {
    data: practiceLogs, 
    mutate,
  } = useSWR(
    // Only fetch if the user is authenticated
    session ? '/api/practice-session' : null,
    fetcher,
    {
      revalidateOnFocus: false, // Don't revalidate on focus
      dedupingInterval: 5000, // Wait 5 seconds before revalidating
    }
  );

  const handleChipClick = (scaleLabel) => {
    // Split on the first space to separate root note from scale type
    const firstSpaceIndex = scaleLabel.indexOf(' ');
    if (firstSpaceIndex === -1) return; // Invalid format
    
    const root = scaleLabel.slice(0, Math.max(0, firstSpaceIndex));
    const mode = scaleLabel.slice(Math.max(0, firstSpaceIndex + 1)).toLowerCase().trim();;
    
    // Update the fretboard state
    dispatch({ type: 'SET_ROOT', payload: root });
    dispatch({ type: 'SET_MODE', payload: mode });
  };


  // Handle delete log
  const handleDeleteLog = async (date, label) => {
    try {
      // Optimistically update the UI
      const optimisticData = practiceLogs.map((log) => log.date === date ? { ...log, scales: log.scales.filter((s) => s.label !== label) } : log);
      // Update the cache
      mutate(optimisticData, { revalidate: false });
  
      const res = await fetch('/api/practice-session', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, label }),
      });
  
      if (!res.ok) throw new Error('Failed to delete scale');
  
      toast.success('Scale deleted!');
  
      // Revalidate the cache
      await mutate();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete scale');
      // Revalidate to restore the correct data
      await mutate();
    }
  };

  if (!practiceLogs?.length) {
    return (
      <div className="text-center p-8 border border-neutral rounded-lg bg-base-100 mx-4 my-4">
        <p className="text-lg">No practice logs yet.</p>
        <p className="text-sm text-base-content/70 mt-2">Start practicing scales to see your progress here!</p>
      </div>
    );
  }

  // Group logs by date to prevent duplicate date sections
  // eslint-disable-next-line unicorn/no-array-reduce
  const groupedLogs = practiceLogs.reduce((acc, log) => {
    const dateTime = DateTime.fromISO(log.date, { zone: 'utc' }).toLocal().startOf('day');
    const dateKey = dateTime.toISO();
    
    if (acc[dateKey]) {
      // Merge scales, avoiding duplicates
      const existingLabels = new Set(acc[dateKey].scales.map(s => s.label));
      for (const scale of log.scales) {
        if (!existingLabels.has(scale.label)) {
          acc[dateKey].scales.push(scale);
          existingLabels.add(scale.label);
        }
      }
    } else {
      acc[dateKey] = {
        dateTime,
        scales: [...log.scales]
      };
    }
    
    return acc;
  }, {});

  // Convert grouped logs to array and sort by date
  const sortedLogs = Object.values(groupedLogs)
    .sort((a, b) => b.dateTime.toMillis() - a.dateTime.toMillis());




  return (
    <div className="border border-accent rounded-lg shadow-sm space-y-4 m-4">
      {sortedLogs.map((log) => {
        const isToday = log.dateTime.hasSame(today, 'day');

        // Skip empty logs
        if (!log.scales?.length) return null;

        return (
          <div
            key={log.dateTime.toISO()}
            className={`p-2 m-2 ${isToday ? 'bg-primary/5' : 'bg-base-100'}`}>
            <div className="flex flex-col gap-2">
              <h2 className="font-semibold text-sm leading-none whitespace-nowrap">
                {isToday ? <span className="text-primary">Today</span> : log.dateTime.toFormat('MMM d')}
              </h2>
              <div className="flex flex-wrap gap-2">
                {/* Sort scales alphabetically for consistent display */}
                {[...log.scales]
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((scale) => (
                    <ScaleChip
                      className={notoMusic.className}
                      key={scale.label}
                      label={toTitleCase(scale.label)}
                      deletable={isToday}
                      onDelete={() => handleDeleteLog(log.dateTime.toISO(), scale.label)}
                      onClick={() => handleChipClick(scale.label)}
                    />
                  ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
